import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import RepeatOneIcon from '@mui/icons-material/RepeatOne';

const SectionTimeline = ({ sections, currentTime, duration, activeSection, loopedSection, onSectionTap, accentColor = '#f97316' }) => {
  const containerRef = useRef(null);
  const activeRef = useRef(null);

  useEffect(() => {
    if (activeRef.current && containerRef.current) {
      const container = containerRef.current;
      const chip = activeRef.current;
      const chipLeft = chip.offsetLeft;
      const chipWidth = chip.offsetWidth;
      const containerWidth = container.offsetWidth;
      const scrollTarget = chipLeft - containerWidth / 2 + chipWidth / 2;
      container.scrollTo({ left: scrollTarget, behavior: 'smooth' });
    }
  }, [activeSection]);

  if (!sections || sections.length === 0) return null;

  const totalDuration = duration || sections[sections.length - 1]?.endTime || 1;

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        gap: '3px',
        px: 2,
        pb: 1.5,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {sections.map((section, idx) => {
        const sectionDuration = section.endTime - section.startTime;
        const widthPercent = (sectionDuration / totalDuration) * 100;
        const isActive = activeSection && activeSection.name === section.name && activeSection.startTime === section.startTime;
        const isLooped = loopedSection && loopedSection.startTime === section.startTime;
        const progress = isActive
          ? Math.min(1, Math.max(0, (currentTime - section.startTime) / sectionDuration))
          : currentTime >= section.endTime ? 1 : 0;

        return (
          <Box
            key={`${section.name}-${idx}`}
            ref={isActive ? activeRef : null}
            onClick={() => onSectionTap && onSectionTap(section)}
            sx={{
              position: 'relative',
              flexShrink: 1,
              flexGrow: widthPercent,
              flexBasis: 0,
              minWidth: 0,
              height: 52,
              borderRadius: '8px',
              overflow: 'hidden',
              cursor: 'pointer',
              border: isActive
                ? `1.5px solid ${accentColor}`
                : isLooped
                  ? `1.5px solid ${accentColor}88`
                  : '1.5px solid rgba(255,255,255,0.08)',
              backgroundColor: 'rgba(255,255,255,0.04)',
              transition: 'border-color 0.2s ease',
              '&:active': { transform: 'scale(0.97)' }
            }}
          >
            <Box sx={{
              position: 'absolute',
              left: 0, top: 0, bottom: 0,
              width: `${progress * 100}%`,
              backgroundColor: isActive ? `${accentColor}30` : 'rgba(255,255,255,0.03)',
              transition: 'width 0.1s linear'
            }} />

            <Box sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              px: 1,
              gap: 0.5
            }}>
              {isLooped && (
                <RepeatOneIcon sx={{ fontSize: 16, color: accentColor, flexShrink: 0 }} />
              )}
              <Typography sx={{
                fontSize: '0.85rem',
                fontWeight: isActive ? 700 : 500,
                color: isActive ? 'white' : 'rgba(255,255,255,0.6)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.2
              }}>
                {section.name}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default SectionTimeline;
